import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import de from '@angular/common/locales/de';
import fr from '@angular/common/locales/fr';
import nl from '@angular/common/locales/nl';
import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule, isDevMode } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import * as Sentry from '@sentry/angular';
import { de as DateFnsDE, fr as DateFnsFR, nlBE as DateFnsNL } from 'date-fns/locale';
import { NZ_DATE_LOCALE, NZ_I18N, de_DE, fr_BE, nl_BE } from 'ng-zorro-antd/i18n';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { canRedirect } from './core/utils/redirect-whitelist.utils';
import { AuthenticationModule } from './modules/authentication/authentication.module';
import { AuthenticationService } from './modules/authentication/services/authentication.service';

registerLocaleData(nl);
registerLocaleData(fr);
registerLocaleData(de);

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    BrowserAnimationsModule,
    CoreModule,
    AuthenticationModule,
    NzLayoutModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
  providers: [
    // Allows for typing inside nz-date-picker inputs
    {
      provide: NZ_DATE_LOCALE,
      useFactory: (localeId: string) => {
        switch (localeId) {
          case 'fr':
            return DateFnsFR;
          case 'de':
            return DateFnsDE;
          default:
            return DateFnsNL;
        }
      },
      deps: [LOCALE_ID],
    },
    {
      provide: NZ_I18N,
      useFactory: (localeId: string) => {
        switch (localeId) {
          case 'fr':
            return fr_BE;
          case 'de':
            return de_DE;
          default:
            return nl_BE;
        }
      },
      deps: [LOCALE_ID],
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    Title,
    { provide: APP_INITIALIZER, useFactory: initializeApp, deps: [AuthenticationService, Router], multi: true },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}

function initializeApp(authService: AuthenticationService): () => Observable<boolean> {
  return () =>
    authService.queryAuthentication$().pipe(
      catchError((error) => {
        // We check canRedirect so we don't redirect /intervention-map (used by ITF) to the login screen.
        if (error?.status === 401 && canRedirect(window.location.href)) {
          authService.removeUserSession();
          authService.saveRedirectUrl(window.location.href);
        }
        return of(false);
      })
    );
}
